import { createAsyncThunk, createSlice, createAction } from "@reduxjs/toolkit";
import { RootState } from "../../../core/presentation/store";
import { UserSliceState } from "./types";
import { userModuleContainer } from "../../UserModule";
import { AuthStorage } from "../../../core/presentation/services/AuthTokenService";
import User, { UpdateShipperPayload, UpdateVehicleOwnerPayload, VehicleTypeBasedInterface } from "user/business/User";
import UserUseCase from "user/application/UserUseCase";
import { UpdateDriverPayload } from "fleet/business/Fleet";
import { FleetModule, fleetModuleContainer } from "fleet/FleetModule";
import { groupPlansByName } from "core/presentation/utils/Helper";
import { loadSidebarPermissions, saveSidebarPermissionsToCache, clearSidebarPermissions } from "../../../core/presentation/utils/sidebarPermissionsCache";
import { FCM_TOKEN } from "core/presentation/constants/Auth";
export interface Logout {
  id: any;
  values: any;
}

const cachedSidebarPermissions = loadSidebarPermissions();
const initialState: UserSliceState = {
  isLoading: false,
  isPointLoading: false,
  platformAdmin: [],
  platformAdminDetails: [],
  drivers: [],
  vehicleOwner: [],
  numberOfVehicles: [],
  shippersCount: 0,
  speed: {},
  distanceBasedFee: {
    isActive: false,
    rules: [],
  },
  percentageBasedFee: {
    isActive: false,
    rules: [],
  },
  incrementalBasedFee: {
    isActive: false,
    rules: [],
  },
  devices: [],
  locations: [],
  userDetails: null,
  vehicleTypeBasedFee: {
    isActive: false,
    rules: [],
  },
  shippers: [],
  allShippersDropdown: [],
  roles: [],
  rolesCount: 0,
  rolePermission: {},
  permissions: [],
  shippersDetails: [],
  usersCount: 0,
  vehicleOwnerCount: 0,
  error: null,
  token: "",
  userProfile: {
    userId: 0,
    firstName: "",
    middleName: "",
    lastName: "",
    contactNumber: 0,
    loginMethod: "",
    roles: [],
    permissions: [],
    address: {
      addressId: 0,
      streetAddress: "",
      administrativeDivision: {
        administrativeDivisionId: 0,
        state: "",
        district: "",
        municipality: "",
      },
    },
  },
  user: {
    userId: 0,
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    contactNumber: 0,
    loginMethod: "",
    verificationCode: { value: 0 },
    username: "",
    accountStatus: "",
    roles: [],
    permissions: [],
    tcAgreed: false,
    address: {
      addressId: 0,
      streetAddress: "",
      administrativeDivision: {
        administrativeDivisionId: 0,
        state: "",
        district: "",
        municipality: "",
      },
    },
  },
  administrativeDivision: [],
  isLoggedIn: false,
  hashKey: "",
  digitalContract: "",
  dashboardData: {},
  driverCount: 0,
  showHeader: false,
  estimatedRule: "",
  optimizedVehicleOwners: undefined,
  shipmentDispatchReport: [],
  orderMatchingCriteria: undefined,
  subscription_plan: undefined,
  features: undefined,
  subscription_plan_price: undefined,
  coolingPeriods: undefined,
  isCoolingPeriodsLoading: false,
  taxRate: undefined,
  promotions: undefined,
  promotionsCount: 0,
  contents: null,
  contentCounts: 0,
  contentDetail: "",
  pointRefundRequests: [],
  pointRefundRequestsCount: 0,
  awaitingAccountApproval: [],
  awaitingAccountApprovalCount: 0,
  paymentPartners: [],
  paymentPartnersCount: 0,
  userContractHistory: [],
  contractHistoryCount: 0,
  contract: [],
  contractCount: 0,
  pointsHistory: [],
  pointsCount: 0,
  deleteRequestedOwners: [],
  deleteRequestedDrivers: [],
  deleteRequestedOwnerCount: 0,
  deleteRequestedDriverCount: 0,
  isDeletedOwnerLoading: false,
  isDeletedDriverLoading: false,
  serverLogData: [],
  serverLogDataCount: 0,
  cachedSidebarPermissions,
  isSidebarPermissionsStale: !!cachedSidebarPermissions,
};

export const updateTokenThunk = createAsyncThunk<null, any, { state: RootState }>("user/update-token", async (data: any) => {
  await AuthStorage.setSession(data);
  return data;
});

export const createOtpThunk = createAsyncThunk<User, number, { state: RootState }>("user/createotp", async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.createOtp(values);
});

export const userSignUp = createAsyncThunk<any, any, { state: RootState }>("user/register", async (data: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = useCase.signup(data);
  return response;
});

export const vehicleOwnerSignUp = createAsyncThunk<any, any, { state: RootState }>("user/register-vehicle-owner", async (data: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = useCase.vehicleOwnerSignup(data);
  return response;
});

export const updateUserThunk = createAsyncThunk<User | any, any, { state: RootState }>("user/update-user", async (data: any) => {
  return data;
});

export const userVerifyEmail = createAsyncThunk<any, any, { state: RootState }>("user/verifyEmail", async (data: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = useCase.verifyEmail(data);
  return response;
});

const verifyNumbrThunk = async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  try {
    const data = await useCase.verifyNumber(values);
    return data;
  } catch (error) {
    return error;
  }
};

export const sendCode = createAsyncThunk<any, any, { state: RootState }>("user/sendCode", async (data: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = useCase.sendCode(data);
  return response;
});
const verifyUsername = async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  try {
    const data = await useCase.verifyUsername(values);
    return data;
  } catch (error) {
    return error;
  }
};

const resetPassword = async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  try {
    const data = await useCase.resetPassword(values);
    return data;
  } catch (error) {
    return error;
  }
};

const reverseGeoCode = async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  try {
    const data = await useCase.reverseGeoCode(values);
    return data;
  } catch (error) {
    return error;
  }
};

export const userLoginThunk = createAsyncThunk<any, any, { state: RootState }>("user/userlogin", async (data: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = useCase.login(data);
  return response;
});

const acceptTermsAndCondition = async (): Promise<null | any> => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  try {
    return await useCase.acceptTerms();
  } catch (error) {
    return error;
  }
};
const acceptTermsAndConditionWithoutToken = async (values: any): Promise<null | any> => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  try {
    return await useCase.acceptTermsWithoutToken(values);
  } catch (error) {
    return error;
  }
};
const userLoginWitPasswordThunk = createAsyncThunk<any, any, { state: RootState }>("user/userloginWithPassword", async (data: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.loginWithPassword(data);
  await AuthStorage.setSession(response?.data?.data);
  return response;
});

export const getRefreshTokenThunk = createAsyncThunk<any, void, { state: RootState }>("user/getrefreshToken", async (any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getRefreshToken();

  await AuthStorage.setSession(response?.data?.data);
  return response;
});

export const setUserPassword = createAsyncThunk<any, any, { state: RootState }>("user/setUserPassword", async (data: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = useCase.setPassword(data);
  return response;
});

export const setAuthUserPasswordThunk = createAsyncThunk<any, any, { state: RootState }>("user/setAuthUserPasswordThunk", async (data: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = useCase.setAuthUserPassword(data);
  return response;
});

const updateAuthenticationMethod = async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  try {
    return await useCase.updateAuthMethod(values);
  } catch (error) {
    return error;
  }
};

const setFirstLogin = async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  try {
    return await useCase.updateIsFirstLogin(values);
  } catch (error) {
    return error;
  }
};

const setIsLoggedIn = createAction<boolean>("user/setIsLoggedIn");

const showHeader = createAction<boolean>("user/show-headert");

export const createPlatformAdminThunk = createAsyncThunk<any, any, { state: RootState }>("user/createPlatformAdmin", async (data: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = useCase.createPlatformAdmin(data);

  return response;
});

export const getAllPlatformAdminThunk = createAsyncThunk<any, { page: number; input: string; filter: string }, { state: RootState }>("users/platformAdmins", async ({ page, input, filter }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getAllPlatformAdmin(page, input, filter);
  return response;
});

export const getNoOfVehiclesThunk = createAsyncThunk<any, void, { state: RootState }>("users/numberOfVehicles", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getNoOfVehicles();
  return response;
});

export const getAPlatformAdminThunk = createAsyncThunk<any, any, { state: RootState }>("users/platform-admin-details", async (id: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getAPlatformAdmin(id);
  return response;
});

export const updateAuthorizedBidThunk = createAsyncThunk<any, { state: RootState }>("fleet/assign-driver-to-vehicle", async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.assignDriver(values);
});

export const updatePlatformAdminThunk = createAsyncThunk<any, UpdateVehicleOwnerPayload, { state: RootState }>("users/update-platform-admin", async ({ id, values }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updatePlatformAdmin(id, values);
  return response.data;
});
export const deletePlatformAdminThunk = createAsyncThunk<any, any, { state: RootState }>("users/delete-platform-admin", async (id: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.deletePlatformAdmin(id);
  return response.data;
});

export const getUserProfileThunk = createAsyncThunk<any>("users/currentUser", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.getUserProfile();
});
export const updateUserProfileThunk = createAsyncThunk<User | any, any, { state: RootState }>("users/profile", async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.updateUserProfile(values);
});

export const additonalInfoVehicleOwnerThunk = createAsyncThunk<User | any, any, { state: RootState }>("users/profile-vehicle-owner", async ({ values, id }: { values: any; id: number }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.additonalInfoVehicleOwner(values, id);
});

export const getAdministrativeDivisionThunk = createAsyncThunk<any>("administrativeDivision", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.getAdministrativeDivision();
});

const updatePassword = async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  try {
    const data = await useCase.updatePassword(values);
    return data;
  } catch (error) {
    return error;
  }
};

// export const userLogout = createAsyncThunk<any, void, { state: RootState }>(
//   "user/userLogout",
//   async (any) => {
//     return await AuthStorage.removeSession();
//   }
// );

export const messageReceivedFromSocketThunk = createAsyncThunk<any, { state: RootState }>("socket/message-received", async (values: any) => {
  return;
});

export const userLogout = createAsyncThunk<any, { id: any; isLogin: boolean }, { state: RootState }>("user/userLogout", async ({ id, isLogin }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const fcmToken = localStorage.getItem(FCM_TOKEN);
  const response = await useCase.logout(id, {
    isLogin: isLogin,
    fcmToken: fcmToken,
  });

  if (response.data.success == true) {
    return await AuthStorage.removeSession();
  } else {
    return console.log("logout failed");
  }
});

const getShipperThunk = async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  try {
    const data = await useCase.getShipper();
    return data;
  } catch (error) {
    return error;
  }
};

export const getAllPermissionsThunk = createAsyncThunk<any, void, { state: RootState }>("users/permissions", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getAllPermissionsList();
  return response;
});

export const getAllRolesListThunk = createAsyncThunk<any, { page: number; input: string; filter: string }, { state: RootState }>("users/roles", async ({ page, input, filter }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getAllRolesList(page, input, filter);
  return response;
});

export const getRolePermissionThunk = createAsyncThunk<any, any, { state: RootState }>("users/role-permissions", async (id: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getRolePermission(id);
  return response;
});

const assignPermissionsThunk = async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  try {
    const data = await useCase.assignPermissions(values);
    return data;
  } catch (error) {
    return error;
  }
};

const getAllDeviceLocationThunk = createAsyncThunk<any, any, { state: RootState }>("users/device-location", async (tripId: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);

  return await useCase.getAllDeviceLocation(tripId);
});

// Vehicle Owner
export const getAllVechileOwnerThunk = createAsyncThunk<any, void, { state: RootState }>("auth/vehicle-owners", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getAllVechileOwner();
  return response;
});

export const getAllNewVehicleOwnerThunk = createAsyncThunk<
  any,
  {
    page: number;
    input: string;
    filter: string;
    report: string;
    isLoggedIn?: boolean;
    limit?: number;
  },
  { state: RootState }
>("users/vehicle-owners", async ({ page, input, filter, report, isLoggedIn, limit }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getAllNewVechileOwner(page, input, filter, report ?? "", isLoggedIn, limit);
  return response;
});

// const createVechileOwnerThunk = createAsyncThunk<
//   any,
//   any,
//   { state: RootState }
// >("users/vehicle-owners", async (values: any) => {
//   const useCase = userModuleContainer.getProvided(UserUseCase);
//   return await useCase.createVechileOwner(values);
// });

const createVechileOwnerThunk = async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  try {
    const data = await useCase.createVechileOwner(values);
    return data;
  } catch (error) {
    return error;
  }
};

export const getAUserThunk = createAsyncThunk<any, any, { state: RootState }>("users/vehicle-owners-details", async (id: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getAUser(id);
  return response;
});

export const getVehiclesOfVehicleOwnerThunk = createAsyncThunk<any, any, { state: RootState }>("users/vehicle-owners-vehicles-details", async ({ contactNumber: contactNumber, page: page }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getVehiclesOfVehicleOwner(contactNumber, page);
  return response;
});

export const getPointsOfVehicleOwnerThunk = createAsyncThunk<any, any, { state: RootState }>("users/vehicle-owners-point-purchase-details", async ({ userId: userId, page: page }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getPointsOfVehicleOwner(userId, page);
  return response;
});

export const updateVechileOwnerThunk = createAsyncThunk<any, any, { state: RootState }>("users/update-vehicle-owners", async ({ id, values }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updateVechileOwner(id, values);
  return response.data;
});

export const deleteVechileOwnerThunk = createAsyncThunk<any, any, { state: RootState }>("users/delete-vehicle-owners", async (id: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.deleteVechileOwner(id);
  return response.data;
});

export const rejectDeletionRequestThunk = createAsyncThunk<any, any, { state: RootState }>("users/reject-account-deletion-request", async ({ id, values }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.rejectDeletionRequest(id, values);
  return response.data;
});

// Driver

export const getAllDriverThunk = createAsyncThunk<any, any, { state: RootState }>("users/drivers", async ({ page, input, filter, report, limit }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getAllDriver(page, input, filter, report, limit);
  return response;
});

const createDriverThunk = createAsyncThunk<any, any, { state: RootState }>("users/driver", async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.createDriver(values);
});
export const getADriverThunk = createAsyncThunk<any, any, { state: RootState }>("users/driver", async (id: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getADriver(id);
  return response;
});

export const updateDriverThunk = createAsyncThunk<any, UpdateDriverPayload, { state: RootState }>("users/update-driver", async ({ id, values }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updateDriver(id, values);
  return response.data;
});

export const deleteDriverThunk = createAsyncThunk<any, any, { state: RootState }>("users/delete-driver", async (id: any, { rejectWithValue }) => {
  try {
    const useCase = userModuleContainer.getProvided(UserUseCase);
    const response = await useCase.deleteDriver(id);
    if (!response.data.success) {
      return rejectWithValue(response.data);
    }

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

// Shipper

const createShipperThunk = async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  try {
    const data = await useCase.createShipper(values);
    return data;
  } catch (error) {
    return error;
  }
};

export const getAllShipperThunk = createAsyncThunk<any, { page?: number; input?: string; filter?: string; accountStatus?: string; report?: string }, { state: RootState }>("users/shipper", async ({ page, input, filter, accountStatus, report }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getAllShipper(page ?? 0, input ?? "", filter ?? "", accountStatus ?? "", report ?? "");
  return response;
});

export const getAllShippersDropdownThunk = createAsyncThunk<any, void, { state: RootState }>("users/shippers-dropdown", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getAllShipper(0, "", "", "", "");
  return response;
});

export const getAShipperThunk = createAsyncThunk<any, any, { state: RootState }>("users/shipper-details", async (id: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getAShipper(id);
  return response;
});

export const updateShipperThunk = createAsyncThunk<any, any, { state: RootState }>("users/update-shipper", async ({ id, values }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updateShipper(id, values);
  return response.data;
});

export const deleteShipperThunk = createAsyncThunk<any, any, { state: RootState }>("users/delete-shipper", async (id: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.deleteShipper(id);
  return response.data;
});

export const getAllDriverDevices = createAsyncThunk<any, void, { state: RootState }>("users/driver-devices", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getAllDriverDevices();
  return response;
});

//hash key
export const getHashKey = createAsyncThunk<any, void, { state: RootState }>("users/hashkey", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getHashKey();
  return response;
});

export const generateNewHashKey = createAsyncThunk<any, void, { state: RootState }>("users/generateNewHashKey", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.generateNewHash();
});

// Contract
export const getContract = createAsyncThunk<any, { type: string; page: number; limit?: number; search?: string; filter?: string }, { state: RootState }>("users/getContract", async ({ type, page, limit, search, filter }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getContract(type, page, limit, search, filter);
  return response;
});

export const getContractById = createAsyncThunk<any, number, { state: RootState }>("users/getContractById", async (id: number) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getContractById(id);
  return response;
});

export const generateContract = createAsyncThunk<any, any, { state: RootState }>("users/generateContract", async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.generateContract(values);
});

//Digital COntract key
export const getDigitalContract = createAsyncThunk<any, void, { state: RootState }>("users/getDigitalContract", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getDigitalContract();
  return response;
});

const generateDigitalContract = createAsyncThunk<any, any, { state: RootState }>("users/generateDigitalContract", async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.generateDigitalContract(values);
});

// Transaction Fee

// Distance based
export const getAllDistanceBasedFeeDataThunk = createAsyncThunk<any, void, { state: RootState }>("users/get-all-distanceBasedFee-data", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getAllDistanceBasedFee();
  return response;
});

export const getAllVehicleTypeBasedFeeDataThunk = createAsyncThunk<any, void, { state: RootState }>("users/getAll-vehicleTypeRule", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getVehicleTypeBasedRule();
  return response;
});

export const updateVehicleTypeBasedFeeDataThunk = createAsyncThunk<any, { state: RootState }>("users/add-vehicleTypeRule", async (value: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updateVehicleTypeBasedRule(value);
  return response;
});

export const deleteVehicleTypeRuleThunk = createAsyncThunk<any, any, { state: RootState }>("users/delete-VehicleType", async (id: number) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.deleteVehicleTypeRule(id);
  return response;
});

export const addDistanceBasedFeeDataThunk = createAsyncThunk<any, any, { state: RootState }>("users/post-DistanceBased-fee", async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.addDistanceBasedFee(values);
});

export const editDistanceBasedFeeDataThunk = createAsyncThunk<any, any, { state: RootState }>("users/edit-DistanceBased-fee", async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.editDistanceBasedFee(values);
});

export const deleteAllDistanceBasedFeeDataThunk = createAsyncThunk<any, void, { state: RootState }>("users/delete-DistanceBased-fee", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.deleteAllDistanceBasedFee();
});

export const switchDistanceBasedFeeDataThunk = createAsyncThunk<any, any, { state: RootState }>("users/switch-DistanceBased-fee", async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.switchDistanceBasedFee(values);
});

// Percentage based
export const getPercentageBasedFeeDataThunk = createAsyncThunk<any, void, { state: RootState }>("users/get-all-percentageBasedFee-data", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getPercentageBasedFee();
  return response;
});

export const addPercentageBasedFeeDataThunk = createAsyncThunk<any, any, { state: RootState }>("users/add-percentageBased-fee", async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.addPercentageBasedFee(values);
});

export const switchPercentageBasedFeeDataThunk = createAsyncThunk<any, any, { state: RootState }>("users/switch-percentageBased-fee", async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.switchPercentageBasedFee(values);
});

// incremental based
export const getIncrementalBasedFeeDataThunk = createAsyncThunk<any, void, { state: RootState }>("users/get-all-IncrementalBasedFee-data", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getIncrementalBasedFee();
  return response;
});

export const addIncrementalBasedFeeDataThunk = createAsyncThunk<any, any, { state: RootState }>("users/add-IncrementalBased-fee", async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.addIncrementalBasedFee(values);
});

export const switchIncrementalBasedFeeDataThunk = createAsyncThunk<any, any, { state: RootState }>("users/switch-IncrementalBased-fee", async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.switchIncrementalBasedFee(values);
});

// Speed
export const getSpeedDataThunk = createAsyncThunk<any, void, { state: RootState }>("users/speed-data", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getSpeedData();
  return response;
});

export const addMaxSpeedThunk = createAsyncThunk<any, any, { state: RootState }>("users/max-speed", async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.addMaxSpeed(values);
});

export const addNewRolesThunk = createAsyncThunk<any, any, { state: RootState }>("users/addRoles", async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.addNewRoles(values);
});

export const deleteRoleThunk = createAsyncThunk<any, any, { state: RootState }>("users/deleteRoles", async (id: number) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.deleteRole(id);
  return response.data;
});

export const getShipmentTrackingReportThunk = createAsyncThunk<any, any, { state: RootState }>("users/getShipmentTrackingReport", async (orderNumber: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getShipmentTrackingReport(orderNumber);
  return response;
});

export const getShipmentCalenderReportThunk = createAsyncThunk<any, any, { state: RootState }>("users/getShipmentCalendar", async (date: string) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getShipmentCalendarReport(date);
  return response?.data;
});

export const getShipmentDispatchReportThunk = createAsyncThunk<any, { page: number; input: string; filter: string }, { state: RootState }>("users/getShipmentCalendar", async ({ page, input, filter }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getShipmentDispatchReport(page, input, filter);
  return response?.data;
});

export const getDashboardThunk = createAsyncThunk<
  any,
  {
    role: "ROLE_VEHICLE_OWNER" | "ROLE_PLATFORM" | "ROLE_SHIPPER" | "ROLE_OTHER" | "ROLE_STAFF";
  }
>("dashboard/getdashboard", async ({ role }: { role: "ROLE_VEHICLE_OWNER" | "ROLE_PLATFORM" | "ROLE_SHIPPER" | "ROLE_OTHER" | "ROLE_STAFF" }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.getDashboard(role);
});

export const getSkippedAdditionalInfoThunk = createAsyncThunk<any>("users/skippedAdditional", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.getSkippedAdditionalInfo();
});

export const setEstimatedPriceRuleThunk = createAsyncThunk<any, any, { state: RootState }>("users/setEstimatedPriceRule", async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.setEstimatedPriceRule(values);
});

export const getEstimatedPriceRuleThunk = createAsyncThunk<any, void, { state: RootState }>("users/getEstimatedPriceRuleThunk", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getEstimatedPriceRule();
  return response;
});

export const updateEstimatedPriceRuleThunk = createAsyncThunk<any, void, { state: RootState }>("users/updateEstimatedPriceRuleThunk", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updateEstimatedPriceRule();
  return response;
});

// Vehicle Owner
export const getAllVechileOwnerThunkV2 = createAsyncThunk<any, void, { state: RootState }>("auth/vehicle-owners-v2", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getAllVechileOwnerV2();
  return response;
});

export const getAllNewVehicleOwnerDataThunk = createAsyncThunk<any, { page: number; input: string; filter: string; report?: string }, { state: RootState }>("users/vehicle-owners-data", async ({ page, input, filter, report }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getAllNewVehicleOwnerData(page, input, filter, report ?? "");
  return response;
});

//Order Matching Criteria
export const getOrderMatchingCriteriaThunk = createAsyncThunk<any, void, { state: RootState }>("order/get-order-matching-data", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getOrderMatchingCriteria();
  return response;
});
//Update
export const updateOrderMatchingCriteriaThunk = createAsyncThunk<any, { criteriaId: number; values: any }, { state: RootState }>("order/update-order-matching-data", async ({ criteriaId, values }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.updateOrderMatchingCriteria(criteriaId, values);
});

export const getSubscriptionPlanThunk = createAsyncThunk<any, void, { state: RootState }>("subcription/subscription-plans", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getSubscriptionPlan();
  return response;
});

export const getAllFeaturesThunk = createAsyncThunk<any, void, { state: RootState }>("subcription/get-all-features", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getAllFeatures();
  return response;
});

export const updatePlanActiveStatusThunk = createAsyncThunk<any, any, { state: RootState }>("subcription/update-active-status-for-plan", async ({ id, values }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updatePlanActiveStatus(id, values);
  return response.data;
});

export const updatePlanVATPriceThunk = createAsyncThunk<any, any, { state: RootState }>("subcription/update-active-status-price-vat", async ({ id, values }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updatePlanVATPrice(id, values);
  return response.data;
});

export const getSubscriptionPlanDetailsThunk = createAsyncThunk<any, any, { state: RootState }>("subcription/get-plan-details", async (id: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getSubscriptionPlanDetails(id);
  return response;
});

export const getAllContentThunk = createAsyncThunk<any, { page: number; input: string; filter: string }, { state: RootState }>("users/contents", async ({ page, input, filter }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getAllContentThunk(page, input, filter);
  return response;
});

export const getAContentThunk = createAsyncThunk<any, any, { state: RootState }>("users/getAContent", async (id: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = useCase.getAContent(id);
  return response;
});

export const updateGracePeriodThunk = createAsyncThunk<any, any, { state: RootState }>("subcription/update-grace-period", async ({ id, values }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updateGracePeriod(id, values);
  return response.data;
});

export const getPlanPriceThunk = createAsyncThunk<any, void, { state: RootState }>("subcription/get-all-plan-price", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getPlanPrice();
  return response;
});

export const initiateSubscriptionPaymentThunk = createAsyncThunk<any, any, { state: RootState }>("subcription/initiateSubscriptionPayment", async ({ values }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.initiateSubscriptionPayment(values);
  return response.data;
});

export const updateSubscriptionPaymentThunk = createAsyncThunk<any, any, { state: RootState }>("subcription/updateSubscriptionPaymentThunk", async ({ vehicleId, values }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updateSubscriptionPayment(vehicleId, values);
  return response.data;
});

//Contract Owner
export const updateContractOwnerThunk = createAsyncThunk<any, any, { state: RootState }>("users/update-contract-owners", async ({ id, values }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updateContractOwner(id, values);
  return response.data;
});

export const updateContractShipperThunk = createAsyncThunk<any, any, { state: RootState }>("users/update-contract-shippers", async ({ id, values }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updateContractShipper(id, values);
  return response.data;
});

export const clearVehicleOwnerData = createAction("user/clearVehicleOwnerData");

export const getCoolingPeriodsThunk = createAsyncThunk<any, void, { state: RootState }>("user/getCoolingPeriods", async (_, { rejectWithValue }) => {
  try {
    const useCase = userModuleContainer.getProvided(UserUseCase);
    const response = await useCase.getCoolingPeriods();
    return response?.data?.data || [];
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updateCoolingPeriodThunk = createAsyncThunk<any, { coolingPeriodId: number; value: number }, { state: RootState }>("user/updateCoolingPeriod", async ({ coolingPeriodId, value }, { rejectWithValue }) => {
  try {
    const useCase = userModuleContainer.getProvided(UserUseCase);
    const response = await useCase.updateCoolingPeriod(coolingPeriodId, value);
    return response;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const unlockUserThunk = createAsyncThunk<any, number, { state: RootState }>("user/unlockUser", async (userId: number, { rejectWithValue }) => {
  try {
    const useCase = userModuleContainer.getProvided(UserUseCase);
    const response = await useCase.unlockUser(userId);
    return response;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getTaxRateThunk = createAsyncThunk<any, void, { state: RootState }>("user/getTaxRate", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.getTaxRate();
});

export const updateTaxRateThunk = createAsyncThunk<any, { rate: number; taxable: boolean }, { state: RootState }>("user/updateTaxRate", async (values) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.updateTaxRate(values);
});

export const getPromotionsThunk = createAsyncThunk<any, any, { state: RootState }>("vehicle/trips", async ({ page, input, filter }: { page: number; input: string; filter: string }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = useCase.getPromotions(page, input, filter);
  return response;
});
export const updatePromotionStatusThunk = createAsyncThunk<any, { promotionId: number; data: any }, { state: RootState }>("promotion/update-status", async ({ promotionId, data }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updatePromotionStatus(promotionId, data);
  return response;
});

export const sendPromotionAlertThunk = createAsyncThunk<any, { promotionId: number; alertTypes: string[] }, { state: RootState }>("promotion/send-alert", async ({ promotionId, alertTypes }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.sendPromotionAlert(promotionId, alertTypes);
  return response;
});

export const createPromotionThunk = createAsyncThunk<any, any, { state: RootState }>("promotion/create", async (values) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.createPromotion(values);
});

export const updatePromotionThunk = createAsyncThunk<any, { promotionId: number; data: any }, { state: RootState }>("promotion/update", async ({ promotionId, data }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.updatePromotion(promotionId, data);
});

export const getPromotionThunk = createAsyncThunk<any, number, { state: RootState }>("promotion/get", async (promotionId) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  return await useCase.getPromotion(promotionId);
});

export const createContentThunk = createAsyncThunk<any, any, { state: RootState }>("users/createContent", async (values) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.createContent(values);
  return response.data;
});

export const updateContentThunk = createAsyncThunk<any, any, { state: RootState }>("users/updateContent", async ({ id, values }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updateContent(id, values);
  return response.data;
});

export const deleteContentThunk = createAsyncThunk<any, any, { state: RootState }>("users/deleteContent", async (id: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.deleteContent(id);
  return response.data;
});

export const updateOwnProfileThunk = createAsyncThunk<any, any, { state: RootState }>("users/updateOwnProfile", async (values: any) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updateOwnProfile(values);
  return response.data;
});

export const assignPointValueThunk = createAsyncThunk<any, any, { state: RootState }>("users/updateUserPoint", async ({ userId, point, paymentMethod }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updateUserPoint(userId, point, paymentMethod);
  return response.data;
});

export const getPointRefundDetailThunk = createAsyncThunk<any, void, { state: RootState }>("users/pointRefundDetail", async () => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getPointRefundDetail();
  return response.data;
});

export const getPointRefundRequestsThunk = createAsyncThunk<any, any, { state: RootState }>("users/pointRefundRequests", async ({ page, input, filter }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getPointRefundRequests(page, input, filter);
  return response.data;
});

export const pointRefundRequestActionThunk = createAsyncThunk<any, any, { state: RootState }>("users/pointRefundRequestAction", async ({ id, status, reason }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.pointRefundRequestAction(id, status, reason);
  return response.data;
});

export const getPointTransactionLogsThunk = createAsyncThunk<any, any, { state: RootState }>("users/pointTransactionLogsAction", async ({ page, input, filter, limit }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getPointTransactionLogs(page, input, filter, limit);
  return response.data;
});

export const pointRefundRequestThunk = createAsyncThunk<any, any, { state: RootState }>("users/pointTransactionLogsAction", async (amount: number) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.pointRefundRequestThunk(amount);
  return response.data;
});

export const getAwaitingAccountApprovalThunk = createAsyncThunk<any, { page: number; input: string; filter: string }, { state: RootState }>("users/awaiting-account-approval", async ({ page, input, filter }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getAwaitingAccountApproval(page, input, filter);
  return response.data;
});

export const updateAccountApprovalStatusThunk = createAsyncThunk<any, { userId: number; status: "approved" | "rejected" | string }, { state: RootState }>("users/awaiting-account-approval", async ({ userId, status }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updateAccountApprovalStatus(userId, status);
  return response.data;
});

export const updateShipperTripAccessThunk = createAsyncThunk<any, { userId: number; tripAccess: boolean }, { state: RootState }>("users/updateShipperTripAccess", async ({ userId, tripAccess }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updateShipperTripAccess(userId, tripAccess);
  return response.data;
});

export const updateShipperPermissionThunk = createAsyncThunk<any, { userId: number; permission: "normalBidPlacement" | "visibilityOfOrder"; status: boolean }, { state: RootState }>("users/updateShipperPermission", async ({ userId, permission, status }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updateShipperPermission(userId, permission, status);
  return response.data;
});

export const getPaymentCommissionThunk = createAsyncThunk<any, { page: number; search: string }, { state: RootState }>("users/paymentCommission", async ({ page, search }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getPaymentCommission(page, search);
  return response.data;
});

export const updatePaymentCommissionStatusThunk = createAsyncThunk<any, { paymentPartnerId: number; status: boolean }, { state: RootState }>("users/updatePaymentCommissionStatus", async ({ paymentPartnerId, status }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updatePaymentCommissionStatus(paymentPartnerId, status);
  return response.data;
});

export const updatePaymentCommissionThunk = createAsyncThunk<any, { paymentPartnerId: string; values: any }, { state: RootState }>("users/updatePaymentCommission", async ({ paymentPartnerId, values }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.updatePaymentCommission(Number(paymentPartnerId), values);
  return response.data;
});

export const getContractHistoryThunk = createAsyncThunk<any, { userId: number; page: number; limit: number }, { state: RootState }>("users/contractHistory", async ({ userId, page, limit }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getContractHistory(userId, page, limit);
  return response.data;
});

export const getDeleteRequestedVehicleOwnerThunk = createAsyncThunk<any, { page: number; search: string; filter: string }, { state: RootState }>("users/deleteRequestedVehicleOwner", async ({ page, search, filter }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getDeleteRequestedVehicleOwner(page, search, filter);
  return response?.data;
});

export const getDeleteRequestedDriverThunk = createAsyncThunk<any, { page: number; search: string; filter: string }, { state: RootState }>("users/deleteRequestedDriver", async ({ page, search, filter }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getDeleteRequestedDriver(page, search, filter);
  return response?.data;
});

export const getServerLogDataThunk = createAsyncThunk<any, { page: number; limit: number; search: string; filter: string }, { state: RootState }>("users/getServerLogs", async ({ page, limit, search, filter }) => {
  const useCase = userModuleContainer.getProvided(UserUseCase);
  const response = await useCase.getServerLogDataThunk(page, limit, search, filter);
  return response?.data;
});

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(getRefreshTokenThunk.fulfilled, (state, action) => {
      state.token = action?.payload?.accessToken;
      state.isLoggedIn = true;
    });
    builder.addCase(getRefreshTokenThunk.rejected, (state, action) => {
      state.isLoggedIn = false;
      console.error(action.error.message || "unknown error");
    });

    builder.addCase(userLoginThunk.fulfilled, (state, action) => {
      const { success, data } = action?.payload?.data || {};
      if (!!success) {
        state.user = data;
      }
    });

    builder
      .addCase(getAllPlatformAdminThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.platformAdmin = action?.payload?.data?.data?.users;
        state.usersCount = action?.payload?.data?.data?.count;
      })
      .addCase(getAllPlatformAdminThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(getAllPlatformAdminThunk.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getAPlatformAdminThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.platformAdminDetails = action?.payload?.data?.data;
      })
      .addCase(getAPlatformAdminThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(deletePlatformAdminThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.platformAdmin = action?.payload?.data?.data;
      })
      .addCase(deletePlatformAdminThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });

    builder
      .addCase(updatePlatformAdminThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePlatformAdminThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;

        const updatedPlatformAdmin = action.payload;
        state.platformAdmin = state.platformAdmin.map((newplatformAdmin: any) => (newplatformAdmin.id === updatedPlatformAdmin.id ? updatedPlatformAdmin : newplatformAdmin));
      })
      .addCase(updatePlatformAdminThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Update failed";
      });

    builder
      .addCase(getAllRolesListThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.rolesCount = action?.payload?.data?.data?.count;
        state.roles = action?.payload?.data?.data;
      })
      .addCase(getAllRolesListThunk.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllRolesListThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(getAllPermissionsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.permissions = action?.payload?.data?.data;
      })
      .addCase(getAllPermissionsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action?.error?.message || "Unknown error";
      })
      .addCase(getRolePermissionThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.rolePermission = action?.payload?.data;
      })
      .addCase(getRolePermissionThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action?.error?.message || "Unknown error";
      })
      .addCase(getRolePermissionThunk.pending, (state, action) => {
        state.isLoading = true;
      });
    builder.addCase(setUserPassword.fulfilled, (state, action) => {
      const { success, data } = action?.payload?.data || {};
      if (!!success) {
        state.user = data;
      }
    });

    builder.addCase(userSignUp.fulfilled, (state, action) => {
      const { success, data } = action?.payload || {};
      if (!!success) {
        state.user = data;
      }
    });
    builder.addCase(vehicleOwnerSignUp.fulfilled, (state, action) => {
      const { success, data } = action?.payload || {};
      if (!!success) {
        state.user = data;
      }
    });

    builder.addCase(updateUserThunk.fulfilled, (state, action) => {
      state.isLoading = false;
      state.user = action?.payload;
    });

    builder.addCase(userVerifyEmail.fulfilled, (state, action) => {
      const { success, data } = action?.payload?.data || {};
      if (!!success) {
        state.user = data;
      }
    });

    builder.addCase(sendCode.fulfilled, (state, action) => {
      const { success, data } = action?.payload?.data || {};
      if (!!success) {
        // state.user = data;
      }
    });

    builder.addCase(updateTokenThunk.fulfilled, (state, action: any) => {
      state.token = action?.payload?.accessToken;
    });

    builder.addCase(setIsLoggedIn, (state, action) => {
      state.isLoggedIn = action?.payload;
    });

    builder.addCase(getUserProfileThunk.fulfilled, (state, action) => {
      state.userProfile = action?.payload?.data;
      state.isLoggedIn = true;

      state.isSidebarPermissionsStale = false;
      const userId = action?.payload?.data?.user?.userId;
      if(userId){
        saveSidebarPermissionsToCache(userId, action?.payload?.data);
      }
    });

    builder.addCase(getUserProfileThunk.rejected, (state, action) => {
      console.error(action?.error?.message || "unknown error");
      state.isLoggedIn = true;
    });

    builder.addCase(updateUserProfileThunk.fulfilled, (state, action) => {
      state = action?.payload;
      state.isLoggedIn = true;
    });

    builder.addCase(updateUserProfileThunk.rejected, (state, action) => {
      state.isLoggedIn = true;
      console.error(action.error.message || "unknown error");
    });

    builder.addCase(additonalInfoVehicleOwnerThunk.fulfilled, (state, action) => {
      state = action.payload;
      state.isLoggedIn = true;
    });

    builder.addCase(additonalInfoVehicleOwnerThunk.rejected, (state, action) => {
      state.isLoggedIn = true;
      console.error(action.error.message || "unknown error");
    });

    builder.addCase(getDashboardThunk.fulfilled, (state, action) => {
      state.dashboardData = action.payload?.data;
    });
    builder.addCase(getDashboardThunk.rejected, (state, action) => {
      console.error(action.error.message || "unknown error");
    });

    builder.addCase(userLogout.fulfilled, (state, action) => {
      state.isLoggedIn = false;
      state.token = "";
      state.user = {
        userId: 0,
        firstName: "",
        middleName: "",
        lastName: "",
        email: "",
        contactNumber: 0,
        loginMethod: "",
        username: "",
        verificationCode: { value: 0 },
        accountStatus: "",
        roles: [],

        permissions: [],
        tcAgreed: false,
        address: {
          addressId: 0,
          streetAddress: "",
          administrativeDivision: {
            administrativeDivisionId: 0,
            state: "",
            district: "",
            municipality: "",
          },
        },
      };

      clearSidebarPermissions();
      state.cachedSidebarPermissions = null;
      state.isSidebarPermissionsStale = true;
    });

    builder.addCase(getAdministrativeDivisionThunk.fulfilled, (state, action) => {
      state.administrativeDivision = action?.payload?.data;
    });
    builder.addCase(getAdministrativeDivisionThunk.rejected, (state, action) => {
      console.log(action.error.message || "unknown error");
    });
    builder
      .addCase(getAllDeviceLocationThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;

        state.locations = action?.payload?.data?.data;
      })
      .addCase(getAllDeviceLocationThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });

    builder
      .addCase(getAllVechileOwnerThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.vehicleOwner = action?.payload?.data?.data;
      })
      .addCase(getAllVechileOwnerThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(getNoOfVehiclesThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.numberOfVehicles = action?.payload?.data?.data?.vehicles;
      })
      .addCase(getNoOfVehiclesThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })

      .addCase(getAllNewVehicleOwnerThunk.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getAllNewVehicleOwnerThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        // Add to global state only if page !== 0 which returns limited data
        if (action.meta.arg.page !== 0) {
          state.vehicleOwner = action?.payload?.data?.data;
          state.vehicleOwnerCount = action?.payload?.data?.data?.count;
        }
      })
      .addCase(getAllNewVehicleOwnerThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })

      .addCase(getAUserThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.userDetails = action?.payload?.data?.data;
      })
      .addCase(getAUserThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })

      .addCase(getVehiclesOfVehicleOwnerThunk.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getVehiclesOfVehicleOwnerThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(getVehiclesOfVehicleOwnerThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })

      .addCase(deleteVechileOwnerThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        const vehicleOwnerId = action.meta.arg;
        state.vehicleOwner = state?.vehicleOwner?.vehicleOwners?.filter((newvehicleOwner: { userId: any }) => newvehicleOwner.userId !== vehicleOwnerId);
      })
      .addCase(deleteVechileOwnerThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });

    builder
      .addCase(updateVechileOwnerThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateVechileOwnerThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;

        const updatedVehicleOwner = action.payload;
        state.vehicleOwner = state.vehicleOwner?.vehicleOwners?.map((newvehicleOwner: any) => (newvehicleOwner.id === updatedVehicleOwner.id ? updatedVehicleOwner : newvehicleOwner));
      })
      .addCase(updateVechileOwnerThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Update failed";
      });
    builder
      .addCase(getAllDriverThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        // Add to global state only if page !==0 which returns limited data
        if (action.meta.arg.page !== 0) {
          state.driverCount = action?.payload?.data?.data?.count;
          state.drivers = action?.payload?.data?.data?.drivers;
        }
      })
      .addCase(getAllDriverThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(getAllDriverThunk.pending, (state, action) => {
        state.isLoading = true;
      })

      .addCase(deleteDriverThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        const driverId = action.meta.arg;
        state.drivers = state?.drivers?.filter((driver: { driverId: any }) => driver.driverId !== driverId);
      })
      .addCase(deleteDriverThunk.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.message || action.error.message || "Unknown error";
      });

    builder
      .addCase(updateDriverThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateDriverThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;

        const updatedDriver = action.payload;
        state.drivers = state.drivers.map((driver: any) => (driver.id === updatedDriver.id ? updatedDriver : driver));
      })
      .addCase(updateDriverThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Update failed";
      });
    builder
      .addCase(getAllShipperThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.shippers = action?.payload?.data?.data;
        state.shippersCount = action?.payload?.data?.data?.count;
      })
      .addCase(getAllShipperThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(getAllShipperThunk.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getAllShippersDropdownThunk.fulfilled, (state, action) => {
        state.error = null;
        state.allShippersDropdown = action?.payload?.data?.data;
      })
      .addCase(getAllShippersDropdownThunk.rejected, (state, action) => {
        state.error = action.error.message || "Unknown error";
      })
      .addCase(getAShipperThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.shippersDetails = action?.payload?.data?.data;
      })
      .addCase(getAShipperThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(deleteShipperThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        const shipperId = action.meta.arg;
        state.shippers = state?.shippers?.shippers?.filter((shipper: { userId: any }) => shipper.userId !== shipperId);
      })
      .addCase(deleteShipperThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });
    builder
      .addCase(getAllDriverDevices.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.devices = action?.payload?.data?.data;
      })
      .addCase(getAllDriverDevices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });
    builder
      .addCase(updateShipperThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateShipperThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;

        const updatedShipper = action.payload;
        state.shippers = state?.shippers?.shipper?.map((shipper: any) => (shipper.id === updatedShipper.id ? updatedShipper : shipper));
      })
      .addCase(updateShipperThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Update failed";
      });

    builder.addCase(messageReceivedFromSocketThunk.fulfilled, (state, action) => {
      const socketData: any = action?.meta?.arg;
      const tripDetails = state?.devices;

      const tripToUpdate = tripDetails && tripDetails.find((trip: any) => trip?.trip?.tripId === socketData?.content?.tripId);

      if (tripToUpdate && tripToUpdate.device[0] && tripToUpdate.device[0].deviceLocation) {
        const newCoordinates = socketData?.content?.location?.coordinates;

        tripToUpdate.device[0].deviceLocation.location.coordinates = newCoordinates;
        tripToUpdate.device[0].deviceLocation.created_at = socketData?.content?.updateAt;
      } else {
        console.log("Trip not found user.");
      }
    });
    builder.addCase(messageReceivedFromSocketThunk.rejected, (state, action) => {
      console.error(action.error.message || "unknown error");
    });

    // Transaction Fee

    // distance based
    builder
      .addCase(getAllDistanceBasedFeeDataThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.distanceBasedFee = action?.payload?.data?.data.transactionFeePolicies.distanceBased;
      })
      .addCase(getAllDistanceBasedFeeDataThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(getAllDistanceBasedFeeDataThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addDistanceBasedFeeDataThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.distanceBasedFee = action?.payload?.data?.data.transactionFeePolicies.distanceBased;
      })
      .addCase(addDistanceBasedFeeDataThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addDistanceBasedFeeDataThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(editDistanceBasedFeeDataThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.distanceBasedFee = action?.payload?.data?.data.transactionFeePolicies.distanceBased;
      })
      .addCase(editDistanceBasedFeeDataThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(editDistanceBasedFeeDataThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(deleteAllDistanceBasedFeeDataThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.distanceBasedFee = action?.payload?.data?.data.transactionFeePolicies.distanceBased;
      })
      .addCase(deleteAllDistanceBasedFeeDataThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(switchDistanceBasedFeeDataThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.distanceBasedFee = action?.payload?.data?.data.transactionFeePolicies.distanceBased;
      })
      .addCase(switchDistanceBasedFeeDataThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(switchDistanceBasedFeeDataThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      });

    //percentage based
    builder
      .addCase(getPercentageBasedFeeDataThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.percentageBasedFee = action?.payload?.data?.data.transactionFeePolicies.percentageBased;
      })
      .addCase(getPercentageBasedFeeDataThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(getPercentageBasedFeeDataThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addPercentageBasedFeeDataThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.percentageBasedFee = action?.payload?.data?.data.transactionFeePolicies.percentageBased;
      })
      .addCase(addPercentageBasedFeeDataThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(addPercentageBasedFeeDataThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(switchPercentageBasedFeeDataThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.percentageBasedFee = action?.payload?.data?.data.transactionFeePolicies.percentageBased;
      })
      .addCase(switchPercentageBasedFeeDataThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(switchPercentageBasedFeeDataThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      });

    // incremental based
    builder
      .addCase(getIncrementalBasedFeeDataThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.incrementalBasedFee = action?.payload?.data?.data.transactionFeePolicies.incrementalBased;
      })
      .addCase(getIncrementalBasedFeeDataThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(getIncrementalBasedFeeDataThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addIncrementalBasedFeeDataThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        if (action?.payload?.data?.success) {
          state.incrementalBasedFee = action?.payload?.data?.data?.transactionFeePolicies?.incrementalBased;
        }
      })
      .addCase(addIncrementalBasedFeeDataThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(addIncrementalBasedFeeDataThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(switchIncrementalBasedFeeDataThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.incrementalBasedFee = action?.payload?.data?.data.transactionFeePolicies.incrementalBased;
      })
      .addCase(switchIncrementalBasedFeeDataThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(switchIncrementalBasedFeeDataThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      });

    // vehicle type based
    // builder
    //   .addCase(
    //     getAllVehicleTypeBasedFeeDataThunk.fulfilled,
    //     (state, action) => {
    //       state.isLoading = false;
    //       state.error = null;
    //       state.vehicleTypeBasedFee =
    //         action?.payload?.data?.data.transactionFeePolicies
    //           ?.vehicleTypeBased;
    //       // console.log(action.payload.data.data.transactionFeePolicies);
    //     }
    //   )
    //   .addCase(getAllVehicleTypeBasedFeeDataThunk.rejected, (state, action) => {
    //     state.isLoading = false;
    //     state.error = action.error.message || "Unknown error";
    //   })
    //   .addCase(getAllVehicleTypeBasedFeeDataThunk.pending, (state) => {
    //     state.isLoading = true;
    //     state.error = null;
    //   });
    // builder
    //   .addCase(addVehicleTypeBasedFeeDataThunk.fulfilled, (state, action) => {
    //     state.isLoading = false;
    //     state.error = null;
    //     state.vehicleTypeBasedFee =
    //       action?.payload?.data?.data.transactionFeePolicies?.VehicleTypeBased;
    //   })
    //   .addCase(addVehicleTypeBasedFeeDataThunk.rejected, (state, action) => {
    //     state.isLoading = false;
    //     state.error = action.error.message || "Unknown error";
    //   })
    //   .addCase(addVehicleTypeBasedFeeDataThunk.pending, (state) => {
    //     state.isLoading = true;
    //     state.error = null;
    //   });
    builder
      .addCase(deleteVehicleTypeRuleThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.vehicleTypeBasedFee = action?.payload?.data?.data.transactionFeePolicies?.VehicleTypeBased;
      })
      .addCase(deleteVehicleTypeRuleThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(deleteVehicleTypeRuleThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      });

    builder
      .addCase(getShipmentDispatchReportThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.shipmentDispatchReport = action?.payload;
      })
      .addCase(getShipmentDispatchReportThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(getShipmentDispatchReportThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      });

    // Speed
    builder
      .addCase(getSpeedDataThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.speed = action?.payload?.data?.data;
      })
      .addCase(getSpeedDataThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });
    builder
      .addCase(addMaxSpeedThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.speed = action?.payload?.data?.data;
      })
      .addCase(addMaxSpeedThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });

    //hash key
    builder
      .addCase(getHashKey.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.hashKey = action?.payload?.data;
      })
      .addCase(getHashKey.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });

    builder
      .addCase(generateNewHashKey.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.hashKey = action?.payload?.data;
      })
      .addCase(generateNewHashKey.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });

    builder
      .addCase(getDigitalContract.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;

        state.digitalContract = action?.payload?.data;
      })
      .addCase(getDigitalContract.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });

    builder
      .addCase(generateDigitalContract.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.digitalContract = action?.payload?.data?.data;
      })
      .addCase(generateDigitalContract.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });

    builder.addCase(updateAuthorizedBidThunk.fulfilled, (state, action) => {
      if (!!action.payload.success) {
        const updatedVehicle = action.payload.data;

        const index = state.drivers.findIndex((driver: any) => String(driver?.driverId) === String(updatedVehicle?.driver?.driverId));
        // If the vehicle is found, update its information
        if (index !== -1) {
          state.drivers[index].bidAuthority = updatedVehicle.driver.bidAuthority;
        }
      }
    });

    //delete role
    builder
      .addCase(deleteRoleThunk.fulfilled, (state, action) => {
        if (action?.payload?.success || action?.payload?.data?.success) {
          state.roles = action?.payload;
        }
      })
      .addCase(deleteRoleThunk.rejected, (state, action) => {
        console.error(action.error.message || "unknown error");
      });

    builder.addCase(showHeader, (state, action) => {
      state.showHeader = action?.payload;
    });

    //estimated price rule
    builder
      .addCase(getEstimatedPriceRuleThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.estimatedRule = action?.payload?.data;
      })
      .addCase(getEstimatedPriceRuleThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(updateEstimatedPriceRuleThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.estimatedRule = action?.payload?.data;
      })
      .addCase(updateEstimatedPriceRuleThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });

    //optimized vehicle owner list
    builder
      .addCase(getAllVechileOwnerThunkV2.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.optimizedVehicleOwners = action?.payload?.data?.data;
      })
      .addCase(getAllVechileOwnerThunkV2.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });

    //Order Matching Criteria
    builder
      .addCase(getOrderMatchingCriteriaThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.orderMatchingCriteria = action?.payload?.data?.data;
      })
      .addCase(getOrderMatchingCriteriaThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });
    //Update Order Matching Criteria
    builder
      .addCase(updateOrderMatchingCriteriaThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.orderMatchingCriteria = action?.payload?.data?.data;
      })
      .addCase(updateOrderMatchingCriteriaThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });
    //Subscription Plan
    builder
      .addCase(getSubscriptionPlanThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.subscription_plan = action?.payload?.data?.data;
      })
      .addCase(getSubscriptionPlanThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action?.error?.message || "Unknown error";
      })
      .addCase(getAllFeaturesThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.features = action?.payload?.data?.data;
      })
      .addCase(getAllFeaturesThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action?.error?.message || "Unknown error";
      })

      //Change Status
      .addCase(updatePlanActiveStatusThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.subscription_plan = action?.payload?.data;
      })
      .addCase(updatePlanActiveStatusThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action?.error?.message || "Unknown error";
      })

      //Subscription Plan Price
      .addCase(getPlanPriceThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        const planData = groupPlansByName(action?.payload?.data?.data);
        state.subscription_plan_price = planData;
      })
      .addCase(getPlanPriceThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action?.error?.message || "Unknown error";
      });

    builder.addCase(clearVehicleOwnerData, (state) => {
      state.vehicleOwner = null;
      state.vehicleOwnerCount = 0;
      state.isLoading = false;
      state.error = null;
    });

    builder
      .addCase(getCoolingPeriodsThunk.pending, (state) => {
        state.isCoolingPeriodsLoading = true;
      })
      .addCase(getCoolingPeriodsThunk.fulfilled, (state, action) => {
        state.isCoolingPeriodsLoading = false;
        state.coolingPeriods = action.payload;
      })
      .addCase(getCoolingPeriodsThunk.rejected, (state) => {
        state.isCoolingPeriodsLoading = false;
        state.coolingPeriods = [];
      })
      .addCase(updateCoolingPeriodThunk.fulfilled, (state, action) => {
        // Optionally update state.coolingPeriods here if needed
      });

    builder
      .addCase(getTaxRateThunk.fulfilled, (state, action) => {
        state.taxRate = action.payload?.data?.data;
      })
      .addCase(updateTaxRateThunk.fulfilled, (state, action) => {
        state.taxRate = action.payload?.data?.data;
      });

    //Promotions
    builder
      .addCase(getPromotionsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.promotions = action?.payload?.data?.data?.promotions || [];
        state.promotionsCount = action?.payload?.data?.data?.count;
      })
      .addCase(getPromotionsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(getPromotionsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePromotionStatusThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePromotionStatusThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;

        // Update the promotion status in the state if the API call was successful
        if (action.payload?.data?.success && state.promotions) {
          const { promotionId, data } = action.meta.arg;

          // Find and update the promotion in the state
          state.promotions = state.promotions.map((promotion: any) => {
            if (promotion.promotionId === promotionId) {
              // Return updated promotion with new data
              return {
                ...promotion,
                ...data,
                isActive: data.isActive !== undefined ? data.isActive : promotion.isActive,
              };
            }
            return promotion;
          });
        }
      })
      .addCase(updatePromotionStatusThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to update promotion status";
      })
      .addCase(sendPromotionAlertThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendPromotionAlertThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(sendPromotionAlertThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to send promotion alert";
      })
      .addCase(createPromotionThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPromotionThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        // Optionally update promotions list if needed
        if (action.payload?.data?.success && state.promotions) {
          state.promotions = [...state.promotions, action.payload.data.data];
        }
      })
      .addCase(createPromotionThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to create promotion";
      })
      .addCase(updatePromotionThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePromotionThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;

        // Update promotion in the state if the API call was successful
        if (action.payload?.data?.success && state.promotions) {
          const { promotionId } = action.meta.arg;

          // Update the promotion in the state
          state.promotions = state.promotions.map((promotion: any) => {
            if (promotion.promotionId === promotionId) {
              return action.payload.data.data;
            }
            return promotion;
          });
        }
      })
      .addCase(updatePromotionThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to update promotion";
      })
      .addCase(getPromotionThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPromotionThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(getPromotionThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch promotion";
      });

    builder
      .addCase(getAllContentThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.contents = action?.payload?.data?.data?.tutorialContents;
        state.contentCounts = action?.payload?.data?.data?.count;
      })
      .addCase(getAllContentThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(getAContentThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.contentDetail = action.payload.data.data;
      })
      .addCase(getAContentThunk.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getAContentThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });
    builder
      .addCase(assignPointValueThunk.fulfilled, (state, action) => {
        state.error = null;
        const updatedPointValue = action?.payload?.data?.updatedPointValue;
        const targetUserId = action?.payload?.data?.userId;
        if (action.payload?.success) {
          if (state.vehicleOwner?.vehicleOwners) {
            state.vehicleOwner.vehicleOwners = state.vehicleOwner?.vehicleOwners.map((vehicleOwner: any) => {
              if (vehicleOwner?.userId?.userId === targetUserId) {
                return {
                  ...vehicleOwner,
                  userId: {
                    ...vehicleOwner.userId,
                    point: updatedPointValue,
                  },
                };
              }
              return vehicleOwner;
            });
          }
          if (state.drivers) {
            state.drivers = state.drivers.map((driver: any) => {
              if (driver?.user?.userId === targetUserId) {
                return {
                  ...driver,
                  user: {
                    ...driver.user,
                    point: updatedPointValue,
                  },
                };
              }
              return driver;
            });
          }
        }
      })
      .addCase(assignPointValueThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });
    builder
      .addCase(getPointRefundRequestsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.pointRefundRequests = action?.payload?.data?.refundRequest;
        state.pointRefundRequestsCount = action?.payload?.data?.count;
      })
      .addCase(getPointRefundRequestsThunk.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getPointRefundRequestsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action?.error?.message || "Unknown error";
      });
    builder
      .addCase(getAwaitingAccountApprovalThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.awaitingAccountApproval = action?.payload?.data?.users;
        state.awaitingAccountApprovalCount = action?.payload?.data?.count;
      })
      .addCase(getAwaitingAccountApprovalThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAwaitingAccountApprovalThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });
    builder
      .addCase(getPaymentCommissionThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.paymentPartners = action?.payload?.data?.partners;
        state.paymentPartnersCount = action?.payload?.data?.count;
      })
      .addCase(getPaymentCommissionThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPaymentCommissionThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(updatePaymentCommissionStatusThunk.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updatePaymentCommissionStatusThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updatePaymentCommissionStatusThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      })
      .addCase(updatePaymentCommissionThunk.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updatePaymentCommissionThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updatePaymentCommissionThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });
    builder
      .addCase(getContractHistoryThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.userContractHistory = action?.payload?.data?.contractHistory;
        state.contractHistoryCount = action?.payload?.data?.count;
      })
      .addCase(getContractHistoryThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getContractHistoryThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });
    builder
      .addCase(getContract.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getContract.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.contract = action?.payload?.data;
        state.contractCount = action?.payload?.data?.count;
      })
      .addCase(getContract.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });

    builder
      .addCase(generateContract.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.contract = action?.payload?.data?.data;
      })
      .addCase(generateContract.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });

    builder
      .addCase(getContractById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getContractById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;

        state.contract = action?.payload?.data;
      })
      .addCase(getContractById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Unknown error";
      });
    builder
      .addCase(getPointsOfVehicleOwnerThunk.pending, (state, action) => {
        state.isPointLoading = true;
        state.error = null;
      })
      .addCase(getPointsOfVehicleOwnerThunk.fulfilled, (state, action) => {
        state.isPointLoading = false;
        state.pointsHistory = action?.payload?.data?.data?.points;
        state.pointsCount = action?.payload?.data?.data?.count;
        state.error = null;
      })
      .addCase(getPointsOfVehicleOwnerThunk.rejected, (state, action) => {
        state.isPointLoading = false;
        state.error = action.error.message || "Unknown error";
      });
    builder
      .addCase(getDeleteRequestedVehicleOwnerThunk.pending, (state, action) => {
        state.isDeletedOwnerLoading = true;
        state.error = null;
      })
      .addCase(getDeleteRequestedVehicleOwnerThunk.fulfilled, (state, action) => {
        state.isDeletedOwnerLoading = false;
        state.deleteRequestedOwners = action?.payload?.data?.deleteRequestedVehicleOwner;
        state.deleteRequestedOwnerCount = action?.payload?.data?.count;
        state.error = null;
      })
      .addCase(getDeleteRequestedVehicleOwnerThunk.rejected, (state, action) => {
        state.isDeletedOwnerLoading = false;
        state.error = action?.error?.message || "Unknown error";
      });
    builder
      .addCase(getDeleteRequestedDriverThunk.pending, (state, action) => {
        state.isDeletedDriverLoading = true;
        state.error = null;
      })
      .addCase(getDeleteRequestedDriverThunk.fulfilled, (state, action) => {
        state.isDeletedDriverLoading = false;
        state.deleteRequestedDrivers = action?.payload?.data?.deleteRequestedDriver;
        state.deleteRequestedDriverCount = action?.payload?.data?.count;
        state.error = null;
      })
      .addCase(getDeleteRequestedDriverThunk.rejected, (state, action) => {
        state.isDeletedDriverLoading = false;
        state.error = action?.error?.message || "Unknown error";
      });
    builder
      .addCase(getServerLogDataThunk.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getServerLogDataThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.serverLogData = action?.payload?.data?.getRejectedData;
        state.serverLogDataCount = action?.payload?.data?.count;
        state.error = null;
      })
      .addCase(getServerLogDataThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action?.error?.message || "Unknown error";
      });
  },
});

export const userSelectors = {
  selectCoolingPeriods: (state: RootState) => state.user.coolingPeriods,
  selectIsCoolingPeriodsLoading: (state: RootState) => state.user.isCoolingPeriodsLoading,
};

export const userActions = userSlice.actions;

export const userThunks = {
  userLoginThunk,
  userSignUp,
  vehicleOwnerSignUp,
  acceptTermsAndCondition,
  userVerifyEmail,
  verifyNumbrThunk,
  setUserPassword,
  updateAuthenticationMethod,
  setFirstLogin,
  setIsLoggedIn,
  userLoginWitPasswordThunk,
  getRefreshTokenThunk,
  updatePassword,
  reverseGeoCode,
  createPlatformAdminThunk,
  getUserProfileThunk,
  updateUserProfileThunk,
  additonalInfoVehicleOwnerThunk,
  getAdministrativeDivisionThunk,
  verifyUsername,
  resetPassword,
  userLogout,
  getShipperThunk,
  sendCode,
  getAllPlatformAdminThunk,
  getAllVechileOwnerThunk,
  getAllNewVehicleOwnerThunk,
  createVechileOwnerThunk,
  getAUserThunk,
  getVehiclesOfVehicleOwnerThunk,
  getPointsOfVehicleOwnerThunk,
  deleteVechileOwnerThunk,
  rejectDeletionRequestThunk,
  updateVechileOwnerThunk,
  getAllDriverThunk,
  createDriverThunk,
  getADriverThunk,
  getNoOfVehiclesThunk,
  deleteDriverThunk,
  updateDriverThunk,
  createShipperThunk,
  getAllShipperThunk,
  getAllShippersDropdownThunk,
  getAShipperThunk,
  deleteShipperThunk,
  updateShipperThunk,
  getAllDeviceLocationThunk,
  getAllRolesListThunk,
  getAllDriverDevices,
  getAllPermissionsThunk,
  messageReceivedFromSocketThunk,
  getRolePermissionThunk,
  assignPermissionsThunk,
  deletePlatformAdminThunk,
  updatePlatformAdminThunk,
  getAPlatformAdminThunk,
  getHashKey,
  generateNewHashKey,
  getContract,
  generateContract,
  getDigitalContract,
  generateDigitalContract,
  updateAuthorizedBidThunk,
  getDashboardThunk,
  getSpeedDataThunk,
  addMaxSpeedThunk,
  getShipmentTrackingReportThunk,
  getShipmentCalenderReportThunk,
  getShipmentDispatchReportThunk,
  addNewRolesThunk,
  deleteRoleThunk,
  setAuthUserPasswordThunk,
  acceptTermsAndConditionWithoutToken,
  showHeader,
  getSkippedAdditionalInfoThunk,
  getAllDistanceBasedFeeDataThunk,
  addDistanceBasedFeeDataThunk,
  editDistanceBasedFeeDataThunk,
  deleteAllDistanceBasedFeeDataThunk,
  switchDistanceBasedFeeDataThunk,
  setEstimatedPriceRuleThunk,
  getEstimatedPriceRuleThunk,
  getAllVehicleTypeBasedFeeDataThunk,
  updateVehicleTypeBasedFeeDataThunk,
  deleteVehicleTypeRuleThunk,
  getPercentageBasedFeeDataThunk,
  addPercentageBasedFeeDataThunk,
  switchPercentageBasedFeeDataThunk,
  addIncrementalBasedFeeDataThunk,
  getIncrementalBasedFeeDataThunk,
  switchIncrementalBasedFeeDataThunk,
  updateEstimatedPriceRuleThunk,
  getAllVechileOwnerThunkV2,
  getAllNewVehicleOwnerDataThunk,
  getOrderMatchingCriteriaThunk,
  updateOrderMatchingCriteriaThunk,
  updateContractOwnerThunk,
  updateContractShipperThunk,
  getSubscriptionPlanThunk,
  getAllFeaturesThunk,
  updatePlanActiveStatusThunk,
  updatePlanVATPriceThunk,
  getSubscriptionPlanDetailsThunk,
  updateGracePeriodThunk,
  getPlanPriceThunk,
  initiateSubscriptionPaymentThunk,
  updateSubscriptionPaymentThunk,
  clearVehicleOwnerData,
  getCoolingPeriodsThunk,
  updateCoolingPeriodThunk,
  unlockUserThunk,
  getTaxRateThunk,
  updateTaxRateThunk,
  getPromotionsThunk,
  updatePromotionStatusThunk,
  sendPromotionAlertThunk,
  createPromotionThunk,
  updatePromotionThunk,
  getPromotionThunk,
  getAllContentThunk,
  getAContentThunk,
  createContentThunk,
  updateContentThunk,
  deleteContentThunk,
  updateOwnProfileThunk,
  assignPointValueThunk,
  getPointRefundDetailThunk,
  getPointRefundRequestsThunk,
  pointRefundRequestActionThunk,
  getPointTransactionLogsThunk,
  pointRefundRequestThunk,
  getAwaitingAccountApprovalThunk,
  updateAccountApprovalStatusThunk,
  updateShipperTripAccessThunk,
  updateShipperPermissionThunk,
  getPaymentCommissionThunk,
  updatePaymentCommissionStatusThunk,
  updatePaymentCommissionThunk,
  getContractHistoryThunk,
  getContractById,
  getDeleteRequestedVehicleOwnerThunk,
  getDeleteRequestedDriverThunk,
  getServerLogDataThunk,
};

export const userReducer = userSlice.reducer;
